import { useContext, useEffect, useState } from 'react'

import { RecurlyPayment } from 'api'
import { useRecurly } from 'lib/recurly'
import { useCheckout, useUpdate } from 'features/checkout/hooks'
import { useUser } from 'hooks/use-user'
import Button from 'components/ui/button'
import { cn } from 'utils/classMerge'
import { Mixpanel, MIXPANEL_PROPS } from 'utils/mixpanel'
import { useGrowthBookFeature } from 'context/growthbook-context'
import { growthbookFeatures } from 'utils/growthbook'

import { CHECKOUT_SUBMIT_TYPE } from '../form/consts'
import { RecurlyContext } from '../../providers/index'

import { ApplePayLogo } from './logo'

const isDev = process.env.NODE_ENV === 'development'

interface ApplePayProps {
  className?: string
  source?: string
  checkoutSuccessSource: string
}

interface PaymentData {
  payment: {
    recurlyToken: RecurlyPayment
  }
}

interface RecurlyApplePayInstance {
  begin: () => void
  ready: (callback: () => void) => void
  on: (event: string, callback: (err: unknown) => void) => void
  onCancel?: () => void
}

interface RecurlyApplePayConfig {
  country: string
  currency: string
  label: string
  total: string
  callbacks: {
    onPaymentAuthorized: (paymentData: PaymentData) => void
  }
}

export const ApplePay = ({
  className,
  source,
  checkoutSuccessSource,
}: ApplePayProps) => {
  const applePayFeature = useGrowthBookFeature(growthbookFeatures.APPLE_PAY)
  const context = useContext(RecurlyContext)
  if (!context) {
    throw new Error('useRecurly must be used within a RecurlyContainer')
  }

  const {
    currentSubscription,
    setIsLoading,
    isLoading,
    tier,
    interval,
    submitType,
  } = context
  const { handleCheckout } = useCheckout()
  const { handleUpdate } = useUpdate()
  const user = useUser()
  const recurly = useRecurly()

  const [applePay, setApplePay] = useState<RecurlyApplePayInstance | null>(null)
  const [isReady, setIsReady] = useState(false)

  const disabled = isLoading || !applePay || isDev

  const handlePaymentAuthorization = ({ payment }: PaymentData) => {
    setIsLoading(true)

    const handlePaymentCallBack =
      submitType === CHECKOUT_SUBMIT_TYPE.update ? handleUpdate : handleCheckout
    handlePaymentCallBack({
      token: payment.recurlyToken,
      paymentMethod: MIXPANEL_PROPS.BUTTON_NAME.values.APPLEPAY,
      checkoutSuccessSource,
    })
  }

  useEffect(() => {
    if (!user || !currentSubscription || isDev) return

    const applePayConfig: RecurlyApplePayConfig = {
      country: user.countryCode || 'US',
      currency: currentSubscription.currency,
      label: 'My Ubersuggest Subscription',
      total:
        submitType === CHECKOUT_SUBMIT_TYPE.update
          ? '0'
          : currentSubscription.unitPrice?.replace(/\D/g, '') || '0',
      callbacks: { onPaymentAuthorized: handlePaymentAuthorization },
    }

    const applePayInstance = recurly.ApplePay(applePayConfig)
    applePayInstance.ready(() => {
      applePayInstance.onCancel = () => setIsLoading(false)
      setIsReady(true)
    })
    applePayInstance.on('error', (err: Error) => {
      setIsLoading(false)
      console.error('Apple Pay error', err)
    })

    setApplePay(applePayInstance)

    return () => {
      setApplePay(null)
      setIsReady(false)
    }
  }, [
    user?.countryCode,
    currentSubscription?.currency,
    currentSubscription?.unitPrice,
  ])

  if ((!isReady && !isDev) || !applePayFeature?.isEnabled) return null

  return (
    <Button
      disabled={disabled}
      variant="legacy-box"
      className={cn(
        'disabled:hover:tw-border-red tw-m-0 tw-flex tw-h-fit tw-min-w-[120px] tw-items-center tw-justify-center !tw-rounded-md tw-px-4 tw-py-[11px] tw-normal-case',
        className,
      )}
      onClick={() => {
        if (!isReady || isLoading) return
        setIsLoading(true)
        Mixpanel.trackPaymentButtonClicked({
          tier,
          planInterval: interval,
          buttonName: MIXPANEL_PROPS.BUTTON_NAME.values.APPLEPAY,
          source,
        })
        applePay?.begin()
      }}
    >
      <ApplePayLogo
        colorClass={disabled ? 'tw-fill-zinc-200' : 'tw-fill-black'}
      />
    </Button>
  )
}
